import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ConstellationMap } from '@/features/mappa/Constellation';
import {
  PROVINCE_NAMES,
  PROVINCE_SIGLE,
  capoluoghi,
  cercaComuni,
} from '@/features/mappa/comuni';
import { useComuni } from '@/features/mappa/useComuni';
import type { ComuneNodo } from '@/api/types';
import { messaggioErrore } from '@/lib/erroreUtente';
import { useDocumentTitle } from '@/lib/hooks';
import { FasceConfigurate } from './FasceConfigurate';
import { HeroNetwork } from './HeroNetwork';
import styles from './HomePage.module.css';

/**
 * Home — allineata alla home del riferimento
 * (https://www.infocomuni.it/puglia/, visual-diff 29/07/2026).
 *
 * Hero a DUE colonne su gradiente navy 145deg:
 *   sinistra (max 600px)  occhiello mono azzurro · H1 Oswald 44/800 ·
 *                         sottotitolo · picker di ricerca con pill provincia
 *   destra (max 640px)    mappa a costellazione (decorativa)
 * più: canvas «rete animata» di sfondo col comando «Disattiva animazione»
 * (WCAG 2.2.2 — l'animazione parte SOLO se il sistema non chiede riduzione
 * del movimento) e l'indicatore di scorrimento in fondo.
 *
 * Sotto l'hero, la sezione «Argomenti del cittadino»: 6 card che scavalcano
 * il bordo dell'hero (margin-top −44px, voluto). Occhiello e H2 sono
 * presenti nella struttura ma visivamente nascosti, come sul riferimento.
 *
 * L'elenco dei 257 comuni NON è più una sezione di pagina: vive nel
 * dropdown «Comuni» dell'header (decisione del committente, 29/07/2026),
 * insieme al callout di copertura dati rimosso con la stessa decisione.
 */

/** Le 6 card «Argomenti del cittadino», nei colori e testi del riferimento. */
const ARGOMENTI = [
  {
    to: '/territorio/esplora/',
    accent: 'argEsplora',
    icona: '🧭',
    titleKey: 'arg_esplora_title',
    titleFallback: 'Esplora',
    descKey: 'arg_esplora_desc',
    descFallback: 'Luoghi, cammini, racconti, eventi e mappa del territorio pugliese.',
  },
  {
    to: '/eventi/',
    accent: 'argEventi',
    icona: '📅',
    titleKey: 'arg_eventi_title',
    titleFallback: 'Eventi',
    descKey: 'arg_eventi_desc',
    descFallback: 'Calendario civile: patronali, fiere e rievocazioni storiche.',
  },
  {
    to: '/servizi/',
    accent: 'argServizi',
    icona: '🏛️',
    titleKey: 'arg_servizi_title',
    titleFallback: 'Servizi pubblici',
    descKey: 'arg_servizi_desc',
    descFallback: 'Anagrafe, tributi, sportelli e pratiche dei 257 comuni.',
  },
  {
    to: '/news/',
    accent: 'argAvvisi',
    icona: '📣',
    titleKey: 'arg_avvisi_title',
    titleFallback: 'Avvisi & News',
    descKey: 'arg_avvisi_desc',
    descFallback: 'Bandi, ordinanze e comunicazioni ufficiali delle amministrazioni.',
  },
  {
    to: '/rete-civica/jobcoin/',
    accent: 'argLavoro',
    icona: '🤝',
    titleKey: 'arg_lavoro_title',
    titleFallback: 'Lavoro',
    descKey: 'arg_lavoro_desc',
    descFallback: 'Banca del tempo, JobCoin e servizi per la comunità.',
  },
  {
    to: '/rete-civica/cittadinanza-attiva/',
    accent: 'argVolontariato',
    icona: '💜',
    titleKey: 'arg_volontariato_title',
    titleFallback: 'Volontariato',
    descKey: 'arg_volontariato_desc',
    descFallback: 'Dona il tuo tempo: volontariato e cittadinanza attiva.',
  },
] as const;

const ANIM_STORAGE_KEY = 'ic-hero-anim';

/** L'animazione parte solo se né il sistema né l'utente chiedono quiete. */
function animazioneInizialeAttiva(): boolean {
  try {
    const salvata = window.localStorage.getItem(ANIM_STORAGE_KEY);
    if (salvata === 'off') return false;
    if (salvata === 'on') return true;
  } catch {
    // localStorage può essere negato: si cade sulla preferenza di sistema.
  }
  if (document.documentElement.classList.contains('a11y-reduce-motion')) return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function HomePage() {
  const { t } = useTranslation();
  useDocumentTitle(t('hero_title_plain', 'Portale dei 257 comuni pugliesi'));

  const [animAttiva, setAnimAttiva] = useState(animazioneInizialeAttiva);
  const [provincia, setProvincia] = useState<string | null>(null);

  const commutaAnimazione = useCallback(() => {
    setAnimAttiva((attiva) => {
      const prossima = !attiva;
      try {
        window.localStorage.setItem(ANIM_STORAGE_KEY, prossima ? 'on' : 'off');
      } catch {
        // Senza storage la scelta vale per la sessione: va bene così.
      }
      return prossima;
    });
  }, []);

  const scorriAiContenuti = useCallback(() => {
    document.getElementById('argomenti')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <>
      <section className={styles.hero}>
        <HeroNetwork attiva={animAttiva} />

        {/* Comando WCAG 2.2.2 per il contenuto in movimento: l'etichetta
            dice sempre COSA FA il bottone (disattiva/attiva). */}
        <button type="button" className={styles.animToggle} onClick={commutaAnimazione}>
          <svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true">
            {animAttiva ? (
              <g fill="currentColor">
                <rect x="1.5" y="1" width="2.4" height="8" />
                <rect x="6" y="1" width="2.4" height="8" />
              </g>
            ) : (
              <path d="M2 1l7 4-7 4Z" fill="currentColor" />
            )}
          </svg>
          {animAttiva
            ? t('hero_anim_off', 'Disattiva animazione')
            : t('hero_anim_on', 'Attiva animazione')}
        </button>

        <div className={`ic-container ${styles.heroInner}`}>
          <div className={styles.heroContent}>
            <p className={styles.heroKicker}>{t('hero_kicker', 'Comuni della Puglia')}</p>
            <h1 className={styles.heroTitle}>
              {t('hero_title_plain', 'I 257 comuni della Puglia, in un posto solo')}
            </h1>
            <p className={styles.heroSub}>
              {t(
                'hero_sub',
                'Servizi, eventi, territorio e rete civica. InfoComuni affianca il sito del tuo comune, non lo sostituisce.',
              )}
            </p>
            <Picker provincia={provincia} onProvincia={setProvincia} />
          </div>

          <div className={styles.heroMap}>
            <ConstellationMap provinciaAttiva={provincia} />
          </div>
        </div>

        <button
          type="button"
          className={styles.scrollHint}
          onClick={scorriAiContenuti}
          aria-label={t('hero_scroll_hint', 'Scorri ai contenuti')}
        >
          <svg viewBox="0 0 26 26" width="26" height="26" aria-hidden="true">
            <polyline
              points="7,9 13,15 19,9"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <svg viewBox="0 0 26 26" width="26" height="26" aria-hidden="true" className={styles.scrollHintSecond}>
            <polyline
              points="7,9 13,15 19,9"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </section>

      {/* «Argomenti del cittadino»: occhiello e H2 nella struttura ma
          visivamente nascosti, ESATTAMENTE come sul riferimento. */}
      <section id="argomenti" className={styles.argomenti} aria-labelledby="argomenti-titolo">
        <div className="ic-container">
          <p className="ic-sr-only">{t('args_kicker', 'Argomenti del cittadino')}</p>
          <h2 id="argomenti-titolo" className="ic-sr-only">
            {t('args_title', 'Cosa cerchi oggi?')}
          </h2>
          <ul className={styles.argGrid} role="list">
            {ARGOMENTI.map((arg) => (
              <li key={arg.to}>
                <Link to={arg.to} className={`${styles.argCard} ${styles[arg.accent]}`}>
                  <span className={styles.argIcon} aria-hidden="true">
                    {arg.icona}
                  </span>
                  <span className={styles.argTitle}>
                    {t(arg.titleKey, arg.titleFallback)}
                  </span>
                  <span className={styles.argDesc}>{t(arg.descKey, arg.descFallback)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Fasce configurate dalla redazione (decisione 82). Si aggiungono in
          coda: quando nessuno ha configurato niente non rendono nulla, e la
          home resta esattamente quella allineata al riferimento. */}
      <FasceConfigurate />
    </>
  );
}

/** Riferimento stabile: un `[]` nuovo a ogni render rifarebbe i calcoli. */
const ELENCO_VUOTO: readonly ComuneNodo[] = [];

/**
 * Picker dell'hero: campo di ricerca + 7 pill provincia + suggerimenti.
 *
 * I suggerimenti sono link REALI alle schede comune, presi dall'elenco
 * dell'API (richiesta condivisa con l'header e la mappa, `useComuni`): a
 * riposo i capoluoghi, filtrati per provincia e per testo digitato. L'invio
 * del form porta alla ricerca completa (`/cerca/?q=…`).
 *
 * Fino al 01/09/2026 i suggerimenti venivano da 257 record compilati nel
 * bundle: link a schede che con la base dati vuota davano «Comune non
 * trovato». Ora caricamento, guasto ed elenco vuoto sono stati DICHIARATI, e
 * un link proposto è un comune che esiste davvero.
 *
 * Quando nessuna riga dichiara il rango di capoluogo — fixture del dataset
 * storico, o backend non ancora aggiornato — a riposo si mostrano i primi
 * comuni in ordine alfabetico, e l'etichetta lo dice: chiamarli «capoluoghi»
 * sarebbe falso.
 *
 * Le pill sono `aria-pressed`: lo stato non è affidato al solo colore
 * (anche alone bianco + peso). La provincia scelta accende i nodi della
 * mappa accanto (legame decorativo, nei due sensi).
 */
function Picker({
  provincia,
  onProvincia,
}: {
  readonly provincia: string | null;
  readonly onProvincia: (sigla: string | null) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const contenitoreRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [aperto, setAperto] = useState(false);

  const stato = useComuni();
  const elenco = stato.status === 'success' ? stato.data.nodi : ELENCO_VUOTO;

  const { suggerimenti, etichetta } = useMemo(() => {
    if (query.trim()) {
      return {
        suggerimenti: cercaComuni(elenco, query, provincia),
        etichetta: t('hero_search_results', 'Comuni trovati'),
      };
    }
    const capi = capoluoghi(elenco);
    if (capi.length > 0) {
      return {
        suggerimenti: provincia ? capi.filter((c) => c.provincia === provincia) : capi,
        etichetta: t('hero_search_capoluoghi', 'Capoluoghi di provincia'),
      };
    }
    return {
      suggerimenti: cercaComuni(elenco, '', provincia),
      etichetta: t('hero_search_alcuni', 'Alcuni comuni'),
    };
  }, [elenco, provincia, query, t]);

  const onSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      const q = query.trim();
      navigate(q ? `/cerca/?q=${encodeURIComponent(q)}` : '/cerca/');
    },
    [navigate, query],
  );

  // I suggerimenti si chiudono solo quando il focus ESCE dal picker:
  // chiuderli sul blur del campo impedirebbe di cliccare i link.
  const onBlur = useCallback((event: React.FocusEvent) => {
    if (!contenitoreRef.current?.contains(event.relatedTarget as Node)) {
      setAperto(false);
    }
  }, []);

  return (
    <div ref={contenitoreRef} className={styles.picker} onBlur={onBlur}>
      <form role="search" className={styles.pickerSearch} onSubmit={onSubmit}>
        <svg viewBox="0 0 22 22" width="22" height="22" aria-hidden="true" className={styles.pickerLens}>
          <circle cx="9.5" cy="9.5" r="6" fill="none" stroke="currentColor" strokeWidth="2" />
          <path d="M14 14l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <label htmlFor="ic-picker-input" className="ic-sr-only">
          {t('hero_search_label', 'Cerca in Puglia')}
        </label>
        <input
          id="ic-picker-input"
          type="search"
          className={styles.pickerInput}
          placeholder={t('hero_search_label', 'Cerca in Puglia')}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setAperto(true);
          }}
          onFocus={() => setAperto(true)}
          autoComplete="off"
        />
      </form>

      <div
        className={styles.pickerPills}
        role="group"
        aria-label={t('hero_filter_province', 'Filtra per provincia')}
      >
        <button
          type="button"
          className={styles.pill}
          aria-pressed={provincia === null}
          onClick={() => onProvincia(null)}
        >
          {t('filter_all', 'Tutte')}
        </button>
        {PROVINCE_SIGLE.map((sigla) => (
          <button
            key={sigla}
            type="button"
            className={styles.pill}
            aria-pressed={provincia === sigla}
            onClick={() => onProvincia(provincia === sigla ? null : sigla)}
          >
            {sigla === 'BAT' ? 'BAT' : (PROVINCE_NAMES[sigla] ?? sigla)}
          </button>
        ))}
      </div>

      {aperto ? (
        <div className={styles.pickerSuggest}>
          <p className={styles.pickerSuggestLabel}>{etichetta}</p>
          {/* Tre assenze diverse, tre messaggi diversi: «sto caricando»,
              «non ho potuto chiedere» e «non c'è nessun comune» non sono la
              stessa cosa per chi legge. */}
          {stato.status === 'loading' ? (
            <p className={styles.pickerSuggestEmpty} role="status">
              {t('loading', 'Caricamento…')}
            </p>
          ) : stato.status === 'error' ? (
            <p className={styles.pickerSuggestEmpty} role="alert">
              {messaggioErrore(stato.error, t).testo}
            </p>
          ) : elenco.length === 0 ? (
            <p className={styles.pickerSuggestEmpty}>
              {t('comuni_none', 'Nessun comune pubblicato al momento.')}
            </p>
          ) : suggerimenti.length === 0 ? (
            /* L'assenza si dichiara, non si mostra un elenco vuoto. */
            <p className={styles.pickerSuggestEmpty}>
              {t('hero_search_none', 'Nessun comune corrisponde alla ricerca.')}
            </p>
          ) : (
            <ul className={styles.pickerSuggestList} role="list">
              {suggerimenti.map((comune) => (
                <li key={comune.slug}>
                  <Link to={`/comuni/${comune.slug}/`} className={styles.pickerSuggestLink}>
                    {comune.nome}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
